import { useVirtual } from "react-virtual";
import { useTranslation } from 'react-i18next';
import { ReactNode, useCallback, useMemo, useRef } from "react";

require('./VirtualTable.css');

export type ColumnProps<T> = {
    render(rowData: T): ReactNode;
    name: string;
};

export type RowProps<T> = {
    rowData: T; // row data as handed to parent div
    cols: ColumnProps<T>[];
};

// eslint-disable-next-line @typescript-eslint/naming-convention -- had to use function rather than const because generics break in .tsx files
function VirtualRow<T>({ rowData, cols }: RowProps<T>) {
    return (
        <>
            { cols.map((col, index) =>
                <div
                    className='grid-column'
                    key={ index }
                >
                    { col.render(rowData) }
                </div>
            ) }
        </>
    );
}

export type Props<T> = {
    id?: string;
    rows: Record<string, T> | T[]; // row data as handed to parent div
    cols: ColumnProps<T>[];
    loading: boolean;
    rowOrder?: string[]; // rowOrder is required if rows is a Record<string, T>. Otherwise, it is ignored when rows is an array
    columnSizes?: string;
    className?: string;
    emptyTableMessage?: string;
    rowElementId?(rowData: T): string;
    rowClassName?(rowData: T): string;
};

// eslint-disable-next-line @typescript-eslint/naming-convention -- had to use function rather than const because generics break in .tsx files
export function VirtualTable<T>({ id, rows, cols, loading, rowOrder = [], columnSizes, className, emptyTableMessage, rowElementId, rowClassName }: Props<T>) {
    const { t } = useTranslation();

    const parentRef = useRef<HTMLDivElement | null>(null);
    const rowCount = Array.isArray(rows) ? rows.length : rowOrder.length;

    const rowVirtualizer = useVirtual({
        size: rowCount,
        parentRef,
    });

    const gridHeaderStyle = useMemo(() => ({ gridTemplateColumns: `${columnSizes} repeat(auto-fit, minmax(0, 1fr))` }), [ columnSizes ]);

    const virtualContainerStyle = useMemo(() => ({ height: `${rowVirtualizer.totalSize}px` }), [ rowVirtualizer.totalSize ]);

    const virtualRowStyle = useCallback(virtualRow => ({
        transform: `translateY(${virtualRow.start}px`,
        gridTemplateColumns: `${columnSizes} repeat(auto-fit, minmax(0, 1fr))`,
        zIndex: rowCount - virtualRow.index,
    }), [ columnSizes, rowVirtualizer.virtualItems ]);

    return (
        <div
            id={ id }
            className={ `${className} grid virtual-table-grid` }
        >
            <div
                className='grid-header'
                style={ gridHeaderStyle }
            >
                { cols.map((col, index) =>
                    <div key={ index }>
                        { col.name }
                    </div>
                ) }
            </div>
            <div
                ref={ parentRef }
                className='grid-body'
            >
                <div
                    style={ virtualContainerStyle }
                >
                    {/* always show loading since react virtualize sometimes takes a second
                        this will be hidden behind the table data when it does load. */}
                    { loading &&
                        <div
                            className='empty-row'
                        >
                            { t('LOADING') }
                            <i className="spinner loading icon"/>
                        </div>
                    }
                    { !rowCount && !loading &&
                        <div
                            className='empty-row no-data-row'
                        >{ emptyTableMessage || ''}
                        </div>
                    }
                    { !loading && rowVirtualizer.virtualItems.map(virtualRow => {
                        const position = rowOrder[virtualRow.index];
                        const rowData = Array.isArray(rows) ? rows[virtualRow.index] : rows[position];
                        return (
                            <div
                                id={ rowElementId && rowElementId(rowData) }
                                className={ `${rowClassName && rowData && rowClassName(rowData)} virtual-row-wrapper` }
                                key={ virtualRow.index }
                                ref={ virtualRow.measureRef }
                                style={ virtualRowStyle(virtualRow) }
                            >
                                <VirtualRow
                                    rowData={ rowData }
                                    cols={ cols }
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
